import type { PaymentRecord } from '../../../types'
import { formatBookingDate, formatBookingTime, formatEuro } from '../../../utils/format'
import { cn } from '../../../utils/cn'
import { CustomerCell } from '../../bookings/components/cells/CustomerCell'
import { RouteCell } from '../../bookings/components/cells/RouteCell'
import { RowActionsMenu, type RowAction } from '../../../components/ui/RowActionsMenu'
import { PaymentMethodCell } from './PaymentMethodCell'
import { PaymentStatusBadge } from './PaymentStatusBadge'
import { PAYMENTS_GRID } from './paymentsTableLayout'

type PaymentRowProps = {
  payment: PaymentRecord
  selected: boolean
  onSelect: () => void
  actions: RowAction[]
}

export const PaymentRow = ({ payment, selected, onSelect, actions }: PaymentRowProps) => {
  const rowLabel = `${payment.bookingId} — ${payment.customer.name}`

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`Select payment ${rowLabel}`}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect()
        }
      }}
      className={cn(
        'grid min-h-[60px] cursor-pointer items-center justify-between rounded-[8px] px-[2px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-dash-lime',
        selected && 'bg-[#F7F7F7]',
      )}
      style={{ gridTemplateColumns: PAYMENTS_GRID }}
    >
      <CustomerCell customer={payment.customer} />

      <span className="whitespace-nowrap px-[4px] font-host text-[12px] font-normal leading-normal text-black">
        {payment.bookingId}
      </span>

      <RouteCell route={payment.route} service={payment.service} />

      <span className="px-[4px] font-host text-[16px] font-semibold leading-normal text-black">
        {formatEuro(payment.amountEur)}
      </span>

      <PaymentMethodCell label={payment.methodLabel} method={payment.method} />

      <PaymentStatusBadge status={payment.status} />

      <div className="flex flex-col items-start gap-[4px] whitespace-nowrap font-host text-[14px] leading-normal">
        <span className="font-medium text-black">{formatBookingDate(payment.paidAt)}</span>
        <span className="font-normal text-dash-cell-muted">{formatBookingTime(payment.paidAt)}</span>
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <RowActionsMenu actions={actions} subject={rowLabel} />
      </div>
    </div>
  )
}
